import { inject, Injectable } from '@angular/core';
import { combineLatest, forkJoin, map, type Observable, of } from 'rxjs';

import {
  ComparisonOperator,
  type LogicalQueryNode,
  LogicalType,
  QueryNodeType,
} from '@blaze-shared/api-contracts-domain';
import { type RuntimeContext } from '@blaze-shared/elements-domain';
import { IntelliSenseService } from '@blaze-shared/intellisense-feature';
import { SchemaService } from '@blaze-shared/schema-data-access';
import {
  type BlazeTableSchema,
  type DatasetRow,
  type DatasetSchema,
  isDatasetLinkValue,
  type WorkflowSchema,
} from '@blaze-shared/schema-domain';
import { type LookupWorkflowNode } from '@blaze-shared/workflow-domain';

import { normalizeNodeInput } from '../../node-input-rows';
import { mergeData } from '../../row-merge';
import { DatasetRowsQueryService } from '../dataset-get/dataset-rows-query.service';

/**
 * Fetches a matching row from another dataset for each input row and merges the two.
 *
 * The join value is evaluated once per input row and the distinct values are fetched in `IN`
 * batches, so a hundred rows sharing ten values cost two requests rather than a hundred.
 */
@Injectable({ providedIn: 'root' })
export class LookupActionService {
  private readonly datasetRowsQueryService = inject(DatasetRowsQueryService);
  private readonly iss = inject(IntelliSenseService);
  private readonly schemaService = inject(SchemaService);

  lookup(
    node: LookupWorkflowNode,
    nodeInput: any,
    ctx: RuntimeContext,
    schema: WorkflowSchema,
    maxBatchSize = 50,
  ): Observable<any[]> {
    const inputData = normalizeNodeInput(nodeInput);
    if (!inputData?.length) {
      return of([]);
    }

    // Keep each row's evaluated join value so the merge step does not have to evaluate it again.
    const joinValues = inputData.map((row) =>
      this.iss.eval(node.joinCondition.value, this.iss.updateRuntimeContext(ctx, row)),
    );

    const lookupRequests = this.batch(Array.from(new Set(joinValues)), maxBatchSize).map((values) =>
      this.datasetRowsQueryService.getDatasetRows({
        condition: '',
        context: ctx,
        datasetId: node.datasetId,
        queryConditions: [],
        queryNode: this.createInQuery(node, values),
        subDataset: node.subDataset ?? '',
      }),
    );

    return combineLatest({
      input2Schema: this.schemaService.obtainSchema<BlazeTableSchema | DatasetSchema>(
        node.datasetId,
      ),
      targetResponses: forkJoin(lookupRequests),
    }).pipe(
      map(({ input2Schema, targetResponses }) => {
        const responseRows = this.indexByJoinColumn(targetResponses, node.joinCondition.column);
        // Version 1 nodes qualify a merged column with `_`, later ones with `.`.
        const mergeSymbol = node?.version == 2 ? '.' : '_';
        return inputData.flatMap((inputRow, i) =>
          mergeData(
            inputRow,
            responseRows.get(joinValues[i]) || {},
            schema,
            input2Schema,
            undefined,
            mergeSymbol,
          ),
        );
      }),
    );
  }

  private batch(values: string[], maxBatchSize: number): string[][] {
    const batches: string[][] = [];
    for (let i = 0; i < values.length; i += maxBatchSize) {
      batches.push(
        values
          .slice(i, i + maxBatchSize)
          .map((value) => this.schemaService.replaceTagIdsWithValuesWhenString(value)),
      );
    }
    return batches;
  }

  private createInQuery(node: LookupWorkflowNode, values: string[]): LogicalQueryNode {
    return {
      nodeType: QueryNodeType.Logical,
      logicalType: LogicalType.And,
      children: [
        {
          column: node.joinCondition.column,
          displayInfos: [],
          isFormulaValue: !!node.joinCondition.isFormulaValue,
          isOptional: false,
          nodeType: QueryNodeType.Comparison,
          operator: ComparisonOperator.In,
          value: JSON.stringify(values),
        },
      ],
    };
  }

  /** Keys the fetched rows by their join value, so each input row finds its match in one lookup. */
  private indexByJoinColumn(
    targetResponses: { data: any }[],
    joinColumn: string,
  ): Map<string, DatasetRow> {
    const responseRows = new Map<string, DatasetRow>();
    const columnName = this.schemaService.getColumnName(joinColumn);
    if (!columnName) {
      return responseRows;
    }
    for (const response of targetResponses) {
      response.data?.forEach((row: DatasetRow) => {
        if (row[columnName]) {
          const key = isDatasetLinkValue(row[columnName])
            ? row[columnName]?.rowId
            : row[columnName];
          responseRows.set(key, row);
        }
      });
    }
    return responseRows;
  }
}
